// src/services/payment.service.ts
import { supabase } from "./supabase";
import { PaymentRecord, InitiatePaymentDTO } from "../types/payment.types";
import { generateId } from "../utils/helpers";
import { cardService } from "./card.service";
import { ENV } from "../config/environment";
import toast from "react-hot-toast";

class PaymentService {
  private readonly TABLE = "payments";

  async recordPayment(
    dto: InitiatePaymentDTO & {
      provider: "paystack" | "flutterwave";
      providerRef: string;
    },
    userId: string,
    userEmail: string
  ): Promise<PaymentRecord> {
    try {
      console.log("Recording payment with DTO:", {
        cardId: dto.cardId,
        amount: dto.amount,
        currency: dto.currency,
        provider: dto.provider,
        providerRef: dto.providerRef,
        userId,
        userEmail,
      });

      // Validate required parameters
      if (!userId) throw new Error("User ID is required");
      if (!userEmail) throw new Error("User email is required");

      // First, verify the card exists
      const card = await cardService.getCard(dto.cardId);
      if (!card) {
        throw new Error(`Card with ID ${dto.cardId} not found`);
      }

      // Verify the user owns this card
      if (card.senderEmail !== userEmail) {
        throw new Error("You do not own this card");
      }

      // Generate payment ID
      const paymentId = generateId();
      const now = new Date().toISOString();

      // Insert payment record
      const { data, error } = await supabase
        .from(this.TABLE)
        .insert({
          id: paymentId,
          card_id: dto.cardId,
          amount: dto.amount,
          currency: dto.currency || ENV.APP_CURRENCY,
          status: "success",
          provider: dto.provider,
          provider_ref: dto.providerRef,
          metadata: {
            ...dto.metadata,
            user_id: userId,
            user_email: userEmail,
          },
          created_at: now,
          updated_at: now,
        })
        .select()
        .single();

      if (error) {
        console.error("Supabase error recording payment:", error);

        // Try to update card status and check if it succeeded
        const updated = await this.forceUpdateCardStatus(dto.cardId, dto.providerRef);

        if (updated) {
          console.log("✅ Card status updated successfully despite payment record error");
        }

        // Return mock payment record
        return {
          id: paymentId,
          cardId: dto.cardId,
          amount: dto.amount,
          currency: dto.currency || ENV.APP_CURRENCY,
          status: "success",
          provider: dto.provider,
          providerRef: dto.providerRef,
          metadata: dto.metadata || {},
          createdAt: now,
          updatedAt: now,
        };
      }

      console.log("Payment recorded successfully:", data);

      // Update card status to paid
      await this.updateCardStatus(dto.cardId, dto.providerRef, userId);

      return this.mapToPaymentRecord(data);
    } catch (error) {
      console.error("Failed to record payment:", error);

      // Even if payment recording fails, try to update card status
      if (dto?.cardId && dto?.providerRef) {
        console.log("Payment recording failed, but attempting to update card status...");
        await this.forceUpdateCardStatus(dto.cardId, dto.providerRef);
      }

      throw error;
    }
  }

  private async updateCardStatus(
    cardId: string,
    providerRef: string,
    userId: string // Change from userEmail to userId
  ): Promise<void> {
    console.log("🔄 updateCardStatus called with:", { cardId, providerRef, userId });

    try {
      // First check if card exists and belongs to user
      const { data: card, error: checkError } = await supabase
        .from("cards")
        .select("id, status")
        .eq("id", cardId)
        .eq("sender_id", userId) // Use sender_id instead of sender_email
        .single();

      if (checkError) {
        console.error("❌ Error checking card:", {
          code: checkError.code,
          message: checkError.message,
        });
        return;
      }

      if (!card) {
        console.error("❌ Card not found or doesn't belong to user");
        return;
      }

      console.log("✅ Card verified for update:", {
        id: card.id,
        currentStatus: card.status,
      });

      // Update card status to paid and set payment_ref
      const { data: updatedData, error: updateError } = await supabase
        .from("cards")
        .update({
          status: "paid",
          payment_ref: providerRef,
          updated_at: new Date().toISOString(),
        })
        .eq("id", cardId)
        .eq("sender_id", userId) // Use sender_id instead of sender_email
        .select()
        .single();

      if (updateError) {
        console.error("❌ Error updating card status:", {
          code: updateError.code,
          message: updateError.message,
          details: updateError.details,
        });

        // Try without select
        const { error: simpleUpdateError } = await supabase
          .from("cards")
          .update({
            status: "paid",
            payment_ref: providerRef,
            updated_at: new Date().toISOString(),
          })
          .eq("id", cardId)
          .eq("sender_id", userId); // Use sender_id here too

        if (simpleUpdateError) {
          console.error("❌ Simple update also failed:", simpleUpdateError);
          toast.error("Payment recorded but failed to update card status");
        } else {
          console.log("✅ Card status updated with simple query with payment_ref:", providerRef);
        }
      } else {
        console.log("✅ Card status updated successfully:", updatedData);
      }
    } catch (error) {
      console.error("❌ Error in updateCardStatus:", error);
    }
  }

  async forceUpdateCardStatus(cardId: string, providerRef: string): Promise<boolean> {
    console.log("🔄 forceUpdateCardStatus called with:", { cardId, providerRef });

    try {
      // First get the card to verify it exists
      const { data: card, error: fetchError } = await supabase
        .from("cards")
        .select("id, sender_id, status")
        .eq("id", cardId)
        .single();

      if (fetchError) {
        console.error("❌ Error fetching card for force update:", {
          code: fetchError.code,
          message: fetchError.message,
        });
        return false;
      }

      if (!card) {
        console.error("❌ Card not found for force update:", cardId);
        return false;
      }

      console.log("✅ Card found for force update:", {
        id: card.id,
        currentStatus: card.status,
        sender_id: card.sender_id,
      });

      // Update card status to paid - WITHOUT .select()
      const { error: updateError } = await supabase
        .from("cards")
        .update({
          status: "paid",
          payment_ref: providerRef,
          updated_at: new Date().toISOString(),
        })
        .eq("id", cardId);

      if (updateError) {
        console.error("❌ Error force updating card status:", updateError);
        return false;
      }

      console.log("✅ Card status force updated successfully with payment_ref:", providerRef);
      return true;
    } catch (error) {
      console.error("❌ Error in forceUpdateCardStatus:", error);
      return false;
    }
  }

  async getPaymentByCardId(cardId: string): Promise<PaymentRecord | null> {
    try {
      const { data, error } = await supabase
        .from(this.TABLE)
        .select("*")
        .eq("card_id", cardId)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching payment:", error);
        return null;
      }

      return data ? this.mapToPaymentRecord(data) : null;
    } catch (error) {
      console.error("Error in getPaymentByCardId:", error);
      return null;
    }
  }

  async updatePaymentStatus(providerRef: string, status: PaymentRecord["status"]): Promise<void> {
    try {
      const { error } = await supabase
        .from(this.TABLE)
        .update({
          status,
          updated_at: new Date().toISOString(),
        })
        .eq("provider_ref", providerRef);

      if (error) {
        console.error("Error updating payment status:", error);
        throw new Error("Failed to update payment status");
      }
    } catch (error) {
      console.error("Error in updatePaymentStatus:", error);
      throw error;
    }
  }

  private mapToPaymentRecord(data: any): PaymentRecord {
    return {
      id: data.id,
      cardId: data.card_id,
      amount: data.amount,
      currency: data.currency,
      status: data.status,
      provider: data.provider,
      providerRef: data.provider_ref,
      metadata: data.metadata || {},
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  }
}

export const paymentService = new PaymentService();
export default paymentService;
